import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ApiUrl } from "./ApiUrl";
import { useLanguage } from "./languageContext";
import translations from "./translation.json";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik } from "formik";
import Loader from "./loader";

const CreateGroup = ({
    isDarkMode,
    groupList,
    setGroupList,
    setSelectedGroup,
    isOpen,
    onClose,
    onGroupCreated,
    bootstrapModalId = "AddDealerModal",
}) => {
    const token = localStorage.getItem("token");
    const { language } = useLanguage();
    const [loading, setLoading] = useState(false);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(
            translations.addPackage.validationText.groupvalidationname[language]
        ),
    });

    const handleSubmit = async (values, { resetForm }) => {
        try {
            setLoading(true);
            const res = await fetch(ApiUrl + "/api/user/create-group", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ name: values.name.trim() }),
            });
            const data = await res.json();

            if (data.status === "success") {
                const newGroup = { label: data.group.name, value: data.group.id };
                toast.success(data.message);
                setGroupList(prevList => [
                    ...prevList,
                    { label: data.group.name, value: data.group.id },
                ]);
                setSelectedGroup(data.group.id);
                 if (onGroupCreated) {
                    onGroupCreated(newGroup);
                    }
                resetForm();
                onClose();
            }
            else if(data.status === "warning"){
              toast.error(translations.alertMessages.alreadyExist[language]);
            } 
            
            else {
                toast.error(translations.alertMessages.wentWrong[language]);
            }
        } catch (error) {
            toast.error(translations.alertMessages.wentWrong[language]);
        } finally {
            setLoading(false);
        }
    };

    // Disable Bootstrap modal focus enforcement when custom modal is open
    useEffect(() => {
        if (isOpen && window.bootstrap) {
            const modalEl = document.getElementById(bootstrapModalId); // first modal
            if (modalEl) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalEl);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalEl);
                }

                // Disable Bootstrap focus enforcement completely
                modalInstance._enforceFocus = function () { };
                modalEl.addEventListener('shown.bs.modal', () => {
                    modalInstance._enforceFocus = function () { };
                });
            }
        }

        // autofocus the first input
        // setTimeout(() => {
        //     const input = document.querySelector("#inputGroupName");
        //     if (input) input.focus();
        // }, 100);
    }, [isOpen, bootstrapModalId]);

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999, // higher than Bootstrap's 1050
            }}
        >
            <div
                style={{
                    zIndex: 2001, // above overlay
                    pointerEvents: "auto",
                    width: "400px",
                    backgroundColor: isDarkMode ? "#212529" : "#fff",
                    borderRadius: "10px",
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                }}
            >
                <Formik
                    initialValues={{ name: "" }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ resetForm, setValues }) => (
                        <Form>
                            {/* Header */}
                            <div
                                style={{
                                    padding: "15px 20px",
                                    borderBottom: isDarkMode
                                        ? "1px solid #495057"
                                        : "1px solid #dee2e6",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    backgroundColor: isDarkMode ? "#343a40" : "#f8f9fa",
                                }}
                            >
                                <h3 style={{ margin: 0, color: isDarkMode ? "#fff" : "#000" }}>
                                    {translations["commonWords"].addPagroup[language]}
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => {
                                        resetForm();
                                        setValues({ name: "" });
                                        onClose();
                                    }}
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        fontSize: "20px",
                                        color: isDarkMode ? "#fff" : "#000",
                                        cursor: "pointer",
                                    }}
                                >
                                    &times;
                                </button>
                            </div>

                            {/* Body */}
                            <div style={{ padding: "20px" }}>
                                <div className="mb-3">
                                    <label
                                        htmlFor="inputGroupName"
                                        className="form-label"
                                        style={{ color: isDarkMode ? "#fff" : "#000" }}
                                    >
                                        {translations.forms.formFields.group[language] +
                                            " " +
                                            translations.commonFields.name[language]}
                                    </label>
                                    <Field
                                        name="name"
                                        type="text"
                                        id="inputGroupName"
                                        className="form-control"
                                        style={{
                                            backgroundColor: isDarkMode ? "#212529" : "#fff",
                                            color: isDarkMode ? "#fff" : "#000",
                                            border: isDarkMode
                                                ? "1px solid #495057"
                                                : "1px solid #dee2e6",
                                            width: "100%",
                                        }}
                                    />
                                    <ErrorMessage
                                        name="name"
                                        component="div"
                                        className="error text-danger"
                                    />
                                </div>
                            </div>

                            {/* Footer */}
                            <div
                                style={{
                                    padding: "15px 20px",
                                    borderTop: isDarkMode
                                        ? "1px solid #495057"
                                        : "1px solid #dee2e6",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "10px",
                                }}
                            >
                                <button
                                    type="submit"
                                    className="admin-btn btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading
                                        ? translations["commonFields"].addingBtn[language]
                                        : translations["commonFields"].addBtn[language]}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        resetForm();
                                        setValues({ name: "" });
                                        onClose();
                                    }}
                                >
                                    {translations["commonFields"].closeBtn[language]}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>

                {loading && <Loader />}
            </div>
        </div>
    );
};

export default CreateGroup;
